// Sales → Access codes (moved from the owner console). A code carries the whole
// deal: kind, staff seats, family licences, the family's price, Zooylo's share,
// SST and currency. Malaysian defaults: RM40 retail, RM30 share, 8% SST, MYR.
// The new code is shown ONCE, with copy. A won prospect arrives prefilled.

import { useState } from "react";
import { useLocation } from "react-router-dom";
import { fill } from "../../i18n/dictionary";
import type { OrgKind } from "../../data/types";
import { Button, Card, ErrorPanel, Field, INPUT, LoadingLine, Notice, TableCard, TD, TH, endOfDay, formatDate, formatMoney, todayIso, useLoad } from "../admin/ui";
import { maskCode } from "../console/ConsoleCodes";
import { ReadOnlyNote, SectionTitle, keeps, salesErrorCopy, shareProblem, useSales } from "./shared";

/** What the pipeline hands over for a won prospect. */
export interface CodePrefill {
  from: string;
  label: string;
  kind: OrgKind;
  families: number | null;
}

export const CODE_DEFAULTS = { retail: "40", share: "30", sst: "8", currency: "MYR", seats: "5", families: "10", uses: "1" } as const;

export function Codes() {
  const { ds, t, can, locale } = useSales();
  const location = useLocation();
  const prefill = (location.state as { prefill?: CodePrefill } | null)?.prefill ?? null;
  const load = useLoad(() => ds.accessCodes(), [ds]);

  const [label, setLabel] = useState(prefill?.label ?? "");
  const [kind, setKind] = useState<OrgKind>(prefill?.kind ?? "organisation");
  const [seats, setSeats] = useState<string>(prefill?.kind === "individual" ? "1" : CODE_DEFAULTS.seats);
  const [families, setFamilies] = useState<string>(prefill?.families != null ? String(prefill.families) : CODE_DEFAULTS.families);
  const [retail, setRetail] = useState<string>(CODE_DEFAULTS.retail);
  const [share, setShare] = useState<string>(CODE_DEFAULTS.share);
  const [sst, setSst] = useState<string>(CODE_DEFAULTS.sst);
  const [currency, setCurrency] = useState<string>(CODE_DEFAULTS.currency);
  const [uses, setUses] = useState<string>(CODE_DEFAULTS.uses);
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  function chooseKind(k: OrgKind) {
    setKind(k);
    if (k === "individual") setSeats("1");
    else if (seats === "1") setSeats(CODE_DEFAULTS.seats);
  }

  async function create() {
    const nums = [seats, families, share, uses].map(Number);
    if (nums.some((n) => !Number.isFinite(n) || n < 0) || Number(uses) < 1 || !/^[A-Za-z]{3}$/.test(currency.trim())) {
      setError(t.common.errInvalid);
      return;
    }
    const problem = shareProblem(t, retail, share, sst);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError("");
    setCreated(null);
    setCopied(false);
    try {
      const code = await ds.createAccessCode({
        label: label.trim(),
        kind,
        seats: Math.max(1, Math.round(Number(seats))),
        familyAllowance: Math.round(Number(families)),
        familyPrice: Number(share),
        familyRetailPrice: retail.trim() === "" ? 0 : Number(retail),
        sstRate: sst.trim() === "" ? 0 : Number(sst),
        currency: currency.trim().toUpperCase(),
        maxUses: Math.max(1, Math.round(Number(uses))),
        expiresAt: expires ? endOfDay(expires) : null,
      });
      setCreated(code);
      setLabel("");
      await load.refresh();
    } catch (err) {
      setError(salesErrorCopy(t, err));
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  const cur = currency.trim().toUpperCase() || "MYR";

  return (
    <section aria-labelledby="codes-title" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <SectionTitle id="codes-title">{t.codes.title}</SectionTitle>
      {can("codes") ? (
        <Card title={t.codes.newCode} id="new-code">
          {prefill && (
            <div style={{ marginBottom: 12 }}>
              <Notice tone="info">{fill(t.codes.prefilled, { name: prefill.from })}</Notice>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 160px), 1fr))", gap: 10 }}>
            <Field label={t.codes.label} value={label} maxLength={200} onChange={(e) => setLabel(e.target.value)} style={{ gridColumn: "1 / -1" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <label htmlFor="sales-code-kind" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-soft)" }}>
                {t.codes.kind}
              </label>
              <select id="sales-code-kind" value={kind} onChange={(e) => chooseKind(e.target.value as OrgKind)} style={INPUT}>
                <option value="organisation">{t.common.kindOrg}</option>
                <option value="individual">{t.common.kindIndividual}</option>
              </select>
            </div>
            <Field label={t.codes.seats} type="number" min={1} value={seats} onChange={(e) => setSeats(e.target.value)} />
            <Field label={t.codes.allowance} type="number" min={0} value={families} onChange={(e) => setFamilies(e.target.value)} />
            <Field label={t.codes.retail} type="number" min={0} step="0.01" inputMode="decimal" value={retail} onChange={(e) => setRetail(e.target.value)} />
            <Field label={t.codes.share} type="number" min={0} step="0.01" inputMode="decimal" value={share} onChange={(e) => setShare(e.target.value)} />
            <Field label={t.codes.keeps} readOnly tabIndex={-1} value={formatMoney(locale, keeps(retail, share), cur)} hint={t.codes.keepsHint} />
            <Field label={t.codes.sst} type="number" min={0} max={100} step="0.01" inputMode="decimal" value={sst} onChange={(e) => setSst(e.target.value)} />
            <Field label={t.codes.currency} maxLength={3} value={currency} onChange={(e) => setCurrency(e.target.value)} />
            <Field label={t.codes.uses} type="number" min={1} value={uses} onChange={(e) => setUses(e.target.value)} />
            <Field label={t.codes.expires} type="date" min={todayIso()} value={expires} onChange={(e) => setExpires(e.target.value)} />
          </div>
          <div style={{ marginTop: 12 }}>
            <Button onClick={() => void create()} disabled={busy}>
              {busy ? t.codes.creating : t.codes.create}
            </Button>
          </div>
          <div role="status" aria-live="polite" style={{ marginTop: error || created ? 12 : 0 }}>
            {error && <p style={{ margin: 0, color: "var(--peach-deep)", fontWeight: 600 }}>{error}</p>}
            {created && (
              <Notice tone="good" title={t.codes.newCodeTitle}>
                <span style={{ display: "block" }}>{t.codes.newCodeBody}</span>
                <span style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
                  <code data-testid="new-code" style={{ fontSize: 20, fontWeight: 700, letterSpacing: ".06em", color: "var(--ink-deep)", overflowWrap: "anywhere" }}>
                    {created}
                  </code>
                  <Button variant="secondary" onClick={() => void copy()}>
                    {copied ? t.codes.copied : t.codes.copy}
                  </Button>
                </span>
              </Notice>
            )}
          </div>
        </Card>
      ) : (
        <ReadOnlyNote />
      )}

      <section aria-labelledby="codes-list-title">
        <h3 id="codes-list-title" style={{ fontSize: 18, color: "var(--ink-deep)", margin: "0 0 10px" }}>
          {t.codes.listTitle}
        </h3>
        {load.loading && !load.data ? (
          <LoadingLine label={t.common.loading} />
        ) : load.error || !load.data ? (
          <ErrorPanel title={t.common.errorTitle} message={salesErrorCopy(t, load.error)} onRetry={load.reload} retryLabel={t.common.retry} />
        ) : load.data.length === 0 ? (
          <Card>
            <p style={{ margin: 0, color: "var(--ink-soft)" }}>{t.codes.none}</p>
          </Card>
        ) : (
          <TableCard label={t.codes.listTitle} minWidth={760}>
            <thead>
              <tr>
                <th scope="col" style={TH}>{t.codes.colCode}</th>
                <th scope="col" style={TH}>{t.codes.colLabel}</th>
                <th scope="col" style={TH}>{t.codes.colKind}</th>
                <th scope="col" style={TH}>{t.codes.colTerms}</th>
                <th scope="col" style={TH}>{t.codes.colUses}</th>
                <th scope="col" style={TH}>{t.codes.colExpires}</th>
              </tr>
            </thead>
            <tbody>
              {load.data.map((c) => (
                <tr key={c.code}>
                  <td style={{ ...TD, fontFamily: "ui-monospace, monospace", whiteSpace: "nowrap" }}>{maskCode(c.code)}</td>
                  <td style={TD}>{c.label || t.common.none}</td>
                  <td style={TD}>{c.kind === "individual" ? t.common.kindIndividual : t.common.kindOrg}</td>
                  <td style={{ ...TD, fontSize: 13.5 }}>
                    {fill(t.codes.termsLine, { seats: c.seatsTotal, families: c.familyAllowance, price: formatMoney(locale, c.familyPriceMonthly, c.currency) })}
                  </td>
                  <td style={{ ...TD, whiteSpace: "nowrap" }}>
                    {c.uses} / {c.maxUses}
                  </td>
                  <td style={{ ...TD, whiteSpace: "nowrap" }}>{c.expiresAt ? formatDate(locale, c.expiresAt) : t.codes.never}</td>
                </tr>
              ))}
            </tbody>
          </TableCard>
        )}
      </section>
    </section>
  );
}
